import { type CollectionSummaryJSONObject, type GeoPoint, IkeToken, type TagSummaryJSONObject } from '@ikegps/core-api';
import { action, computed, observable, makeObservable } from 'mobx';
import { ModalService } from '../../components/modal/modal.service';
import { DragonDomTypes } from '../../dom';
import { T, Translate } from '../../i18n';
import type { TagState } from '../../page/tag.state';
import { Services } from '../../services';
import { MobxState } from '../../state/mobx';
import { CurrentState } from '../../state/page.state';
import { AppStore } from '../../store';
import { PointCache } from '../google/projection.cache';
import { BoundingString } from './bounding.string';

const BOUNDS_EXTEND_DELTA = 0.0005;
const BOUNDS_MIN_DISTANCE_METERS = 100;

const ZOOM_DETAIL_SWITCH_LEVEL = 19;
export const ZOOM_SWITCH_LEVEL = 15;
export const ZOOM_SWITCH_JOBS_LEVEL = 12;

export interface VisibleCollections {
	id: string;
	output: CollectionSummaryJSONObject[];
}

export interface TagMapUrlParams {
	location: string;
}

export class TagMapState extends MobxState {
	tagState: TagState;

	private map: google.maps.Map;

	bounds: google.maps.LatLngBounds;
	boundString = observable.box('', { name: 'BoundsString' });

	@observable zoom: number | null = null;

	constructor(tagState: TagState) {
		super();
		makeObservable(this);

		this.tagState = tagState;

		this.initMapLocation();
	}

	initMapLocation() {
		const locationString = Services.Route.params.get('location');
		const parsedBounds = BoundingString.fromString(locationString);

		if (parsedBounds == null) {
			return;
		}

		const actualBounds = new google.maps.LatLngBounds();
		actualBounds.extend(parsedBounds);

		this.bounds = actualBounds;
		this.zoom = parsedBounds.zoom;
	}

	@action setMap(map: google.maps.Map) {
		this.map = map;
	}

	@computed.struct get visibleTags(): string[] {
		this.boundString.get();
		const tags: string[] = [];

		for (const tag of this.tagState.filteredTags) {
			const tagSummary = AppStore.TagSummary.get(tag.objectId);
			if (tagSummary == null || tagSummary.center == null) {
				continue;
			}

			if (this.bounds == null || this.bounds.contains(tagSummary.center)) {
				tags.push(tag.objectId);
			}
		}

		return tags;
	}

	@computed.struct get visibleCollections(): string[] {
		this.boundString.get();
		// trace();
		if (this.bounds == null) {
			return [];
		}

		const visibleCollections = [];
		const selectedTagId = CurrentState.inspectedTagId;
		const inspectedCollectionRevision = CurrentState.inspectedCollectionRevision;

		let inspectedCollection: string = null;

		for (const tag of this.tagState.filteredTags) {
			const tagSummary = AppStore.TagSummary.get(tag.objectId);
			if (tagSummary == null || tagSummary.center == null || tagSummary.bounds == null) {
				continue;
			}
			const isTagSelected = selectedTagId === tag.objectId;

			const ne = tagSummary.bounds.ne;
			const sw = tagSummary.bounds.sw;
			const isTagInBounds = this.bounds.intersects({
				north: ne.lat,
				east: ne.lng,
				south: sw.lat,
				west: sw.lng,
			});

			if (isTagInBounds === true) {
				for (const revision of tagSummary.revisions) {
					const summary = AppStore.CollectionSummary.get(revision);
					if (summary == null || summary.location == null) {
						continue;
					}

					// Render the inspected collection last
					if (inspectedCollectionRevision === summary.revision) {
						inspectedCollection = summary.revision;
					} else if (isTagSelected) {
						visibleCollections.push(summary.revision);
					} else if (this.bounds.contains(this.getLatLng(summary.location))) {
						visibleCollections.push(summary.revision);
					}
				}
			}
		}

		if (inspectedCollection != null) {
			visibleCollections.push(inspectedCollection);
		}

		return visibleCollections;
	}

	@action.bound centerMap() {
		const visibleBounds = new google.maps.LatLngBounds();

		const summaries: TagSummaryJSONObject[] = [];
		for (const tag of this.tagState.filteredTags) {
			const tagSummary = AppStore.TagSummary.get(tag.objectId);
			if (tagSummary != null) {
				summaries.push(tagSummary);
			}
		}

		if (summaries.length > 0) {
			for (const summary of summaries) {
				if (summary.bounds != null) {
					visibleBounds.extend(summary.bounds.ne).extend(summary.bounds.sw);
				}
			}
		} else {
			visibleBounds.extend({ lat: 80, lng: 180 });
			visibleBounds.extend({ lat: -80, lng: -180 });
		}

		this.map.fitBounds(visibleBounds);
		this.map.setCenter(visibleBounds.getCenter());
	}

	@action setBounds(bounds: google.maps.LatLngBounds) {
		const newBounds = bounds.toUrlValue();
		if (newBounds === this.boundString.get()) {
			return;
		}
		this.bounds = bounds;
		this.boundString.set(bounds.toUrlValue());
	}

	@action setZoom(zoom: number) {
		if (zoom === this.zoom) {
			return;
		}
		if (zoom !== this.zoom) {
			PointCache.reset();
		}
		this.zoom = zoom;
	}

	@action zoomToId(type: string, id: string) {
		if (type === DragonDomTypes.TAG) {
			return this.zoomToTag(id);
		}
	}

	@action zoomToTag(tagId: string): void {
		const tag = this.tagState.filteredTags.find((t) => t.objectId === tagId);
		if (tag == null) {
			return null;
		}

		const tagSummary = AppStore.TagSummary.get(tag.objectId);
		if (tagSummary == null || tagSummary.bounds == null) {
			return null;
		}

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.BigMap, 'Tag', tag.objectId, 1);

		let bounds = new google.maps.LatLngBounds(tagSummary.bounds.sw, tagSummary.bounds.ne);
		bounds = this.ensureAppropriateBounds(bounds);

		this.map.fitBounds(bounds);
	}

	get showDetail(): boolean {
		return this.zoom >= ZOOM_DETAIL_SWITCH_LEVEL;
	}

	@action.bound async handleCollectionClick(event: MouseEvent, summary: CollectionSummaryJSONObject) {
		if (this.tryToggleSelection(event, summary)) {
			return;
		}

		const isHovered = CurrentState.hoverId === summary.revision;
		if (!this.showDetail && !isHovered) {
			return;
		}

		await this.inspectCollection(summary.revision);
	}

	private tryToggleSelection(event: MouseEvent, summary: CollectionSummaryJSONObject): boolean {
		const modifierHeld = event.metaKey || event.ctrlKey;
		const multiSelectEnabled = Services.Token.has(IkeToken.FEATURE_CTRL_SHIFT_SELECT);

		if (!modifierHeld || !multiSelectEnabled) {
			return false;
		}

		const tagId = summary.tags[0];
		if (tagId == null) {
			return false;
		}

		event.stopPropagation();
		if (event.preventDefault) {
			event.preventDefault();
		}

		CurrentState.Tag.toggleCollection(tagId, summary.uuid);
		return true;
	}

	@action private async inspectCollection(revision: string) {
		const collection = AppStore.Collection.get(revision);
		if (collection == null) {
			return;
		}

		// Mark the collection to link to
		if (CurrentState.CollectionLink.isLinking) {
			CurrentState.CollectionLink.completeLinking(collection);
			return;
		}

		// Inspect the collection
		const canChange = await this.checkChangeCollection(collection.revision);
		if (canChange) {
			CurrentState.setInspectedCollection(collection.revision);
		}
	}

	async zoomToCollection(revision: string) {
		if (revision === CurrentState.inspectedCollectionRevision) {
			this.handleZoomToCollection(revision);
			return;
		}

		const canChange = await this.checkChangeCollection(revision);
		if (!canChange) {
			return;
		}

		let newRevision = revision;
		const latestSummary = AppStore.CollectionSummary.getLatest(revision);
		if (latestSummary != null) {
			newRevision = latestSummary.revision;
		}

		CurrentState.setInspectedCollection(newRevision);
		this.handleZoomToCollection(newRevision);
	}

	@action private handleZoomToCollection(revision: string) {
		const collectionSummary = AppStore.CollectionSummary.get(revision);
		if (collectionSummary == null || collectionSummary.location == null) {
			return;
		}

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.BigMap, 'Collection', collectionSummary.uuid, 1);

		let bounds = this.getLatLngBounds(collectionSummary.location);
		bounds = this.ensureAppropriateBounds(bounds);

		if (this.map != null) {
			this.map.fitBounds(bounds);
		}
	}

	checkChangeCollection(revision: string): Promise<boolean> {
		if (CurrentState.inspectedCollectionRevision == null) {
			return Promise.resolve(true);
		}

		if (CurrentState.inspectedCollectionRevision === revision) {
			return Promise.resolve(false);
		}

		return this.checkAndDiscardChanges();
	}

	async checkAndDiscardChanges(): Promise<boolean> {
		if (!CurrentState.CollectionEdit.isDirty && !CurrentState.CollectionLink.hasDirtyTargets) {
			CurrentState.CollectionEdit.reset();
			return true;
		}

		const response = await ModalService.open({
			title: T.DISCARD_CHANGES_TITLE,
			content: Translate(T.DISCARD_CHANGES_CONFIRM),
			okName: T.DISCARD_CHANGES_DISCARD,
			hasCancel: true,
		});

		const summary = AppStore.CollectionSummary.get(CurrentState.inspectedCollectionRevision);
		if (summary != null) {
			Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'Collection:discard', summary.uuid, 1);
		}

		if (response.ok) {
			CurrentState.CollectionEdit.reset();
		}
		return response.ok;
	}

	private ensureAppropriateBounds(bounds: google.maps.LatLngBounds): google.maps.LatLngBounds {
		const distance = google.maps.geometry.spherical.computeDistanceBetween(
			bounds.getNorthEast(),
			bounds.getSouthWest()
		);
		if (distance < BOUNDS_MIN_DISTANCE_METERS) {
			const extendPoint1 = new google.maps.LatLng(
				bounds.getNorthEast().lat() + BOUNDS_EXTEND_DELTA,
				bounds.getNorthEast().lng() + BOUNDS_EXTEND_DELTA
			);
			const extendPoint2 = new google.maps.LatLng(
				bounds.getNorthEast().lat() - BOUNDS_EXTEND_DELTA,
				bounds.getNorthEast().lng() - BOUNDS_EXTEND_DELTA
			);
			bounds.extend(extendPoint1);
			bounds.extend(extendPoint2);
		}

		return bounds;
	}

	private getLatLng(loc: GeoPoint): google.maps.LatLng {
		return new google.maps.LatLng(loc.coordinates[1], loc.coordinates[0]);
	}

	private getLatLngBounds(loc: GeoPoint): google.maps.LatLngBounds {
		return new google.maps.LatLngBounds({
			lat: loc.coordinates[1],
			lng: loc.coordinates[0],
		});
	}
}
